'use client'

import { type ComponentProps, useMemo } from 'react'

import { ArchidektPlaytester } from '@/components/common/ArchidektPlaytester'
import { ButtonDefault } from '@/components/common/Button'
import { CardRec } from '@/components/common/Card'
import { CardCatalog } from '@/components/common/CardCatalog'
import { DecklistBuyLinks } from '@/components/common/DecklistBuyLinks'
import { DescriptionQuill } from '@/components/common/DescriptionQuill'
import { ErrorBlock } from '@/components/common/ErrorBlock'
import { Subheader } from '@/components/common/Subheader'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { type RecType } from '@/defs/rec'
import { usePriceSources } from '@/hooks/usePriceSources'
import { getGalleryResults, parseGalleryDeckText } from '@/utils/galleryUtil'

import styles from './Decklist.module.scss'
import { DecklistDetails } from './DecklistDetails/DecklistDetails'
import { DecklistExport } from './DecklistExport/DecklistExport'

type Props = {
  hideDetails?: boolean
  recs?: RecType[]
  title?: string
  variant?: ComponentProps<typeof CardCatalog>['variant']
}

export const Decklist = ({ hideDetails, recs: recsInput, title = 'Decklist', variant }: Props) => {
  const { page } = usePage()
  const { categories, commanderRec, recs } = page?.decklist || { recs: recsInput }
  const priceSource = usePriceSources()[0]

  const precon = page?.featured?.precon
  const isPrecon = page?.route?.section === 'preconsFeatured' && !!precon

  const { groups } = useMemo(
    () =>
      recs && categories
        ? getGalleryResults({
            cardMap: {},
            categories,
            groupby: 'category',
            priceSource,
            recs,
            sortby: 'default',
            sortbyDirection: 'desc',
          })
        : { groups: [] },
    [categories, recs, priceSource],
  )

  if (!commanderRec || !recs) return <ErrorBlock />

  const decklistText = parseGalleryDeckText(recs)
  const descriptionQuill = page?.metadata?.descriptionQuill

  return (
    <section className={styles.container}>
      <Subheader>
        <div className={styles.subheader}>
          <IconWithLabel iconBefore='cards'>{title}</IconWithLabel>
          <div className={styles.export}>
            <DecklistExport analyticsCategory='decklist' decklist={decklistText} recs={recs}>
              <ButtonDefault colorScheme='muted'>
                <IconWithLabel iconAfter='share'>Export</IconWithLabel>
              </ButtonDefault>
            </DecklistExport>
          </div>
        </div>
      </Subheader>
      <div className={styles.content}>
        <div className={styles.overview}>
          <div className={styles.overviewInner}>
            <div className={styles.overviewSealed}>
              {isPrecon && precon.image ? (
                <img
                  alt={`Image of the ${precon.label || 'Unknown'} sealed product`}
                  className={styles.image}
                  decoding='async'
                  loading='lazy'
                  src={precon.image}
                />
              ) : (
                <CardRec disableLink extend rec={commanderRec} />
              )}
              <div className={styles.overviewActions}>
                <DecklistBuyLinks decklist={decklistText} />
                <ArchidektPlaytester recs={recs} />
                {!hideDetails && <DecklistDetails />}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.catalog}>
          <CardCatalog groups={groups} variant={variant} />
          {!!descriptionQuill?.length && (
            <div className={styles.description}>
              {precon?.label && <h2 className={styles.descriptionHeading}>{precon.label}</h2>}
              <DescriptionQuill deltaOps={descriptionQuill} />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
